/**
 * Header Component for DysTest
 *
 * Language selection is now driven by LanguageContext (global state).
 * The supported language list comes from SUPPORTED_LANGUAGES (static, no backend fetch).
 * Selecting a language calls changeLanguage() from context so all pages update instantly.
 *
 * Author: Uttam
 * Version: 4.0.0
 */

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Microscope, Newspaper, BookOpen, HelpCircle, ArrowRight, ArrowUpRight } from "lucide-react";
import logo from "../../assets/Logo/logo.png";
import { useLanguage } from "../../contexts/LanguageContext";
import { useT } from "../../hooks/useT";
import { nativeLanguageName } from "../../data/supportedLanguages";
import { localizedPublicPath, publicLocaleFromPath, type PublicSeoSection } from '../../data/publicSeo';

const Header: React.FC = () => {
  const { supportedLanguages, currentLanguage, currentLanguageName, changeLanguage } =
    useLanguage();
  const { t } = useT();
  const location = useLocation();
  const navigate = useNavigate();

  // The sign-up form on the home page starts the test: scroll to it there, open the home page at it elsewhere.
  const startScreening = () => {
    setMobileMenuOpen(false);
    if (location.pathname === "/" || (publicLocaleFromPath(location.pathname) && location.pathname.split('/').filter(Boolean).length === 1)) {
      document.getElementById("assessment-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`${localizedPublicPath(currentLanguage, 'home')}#assessment-form`);
    }
  };

  const NAV_LINKS = [
    { label: t('nav_about'), to: localizedPublicPath(currentLanguage, 'about') },
  ] as const;

  const RESOURCES_LINKS = [
    { label: t('nav_techPapers'), to: "/technical-papers", icon: Microscope, isActive: location.pathname.startsWith("/technical-papers") },
    { label: t('nav_blog'),       to: "/blog",             icon: Newspaper,  isActive: location.pathname === "/blog" },
    { label: t('nav_guides'),     to: "/guides",           icon: BookOpen,   isActive: location.pathname.startsWith("/guides") },
    { label: t('nav_faq'),        to: localizedPublicPath(currentLanguage, 'faq'), icon: HelpCircle, isActive: location.pathname.endsWith('/faq') },
  ];
  const resourcesActive = RESOURCES_LINKS.some((r) => r.isActive);

  const [showLanguagePopup, setShowLanguagePopup]   = useState(false);
  const [showResourcesMenu, setShowResourcesMenu]   = useState(false);
  const [scrolled, setScrolled]                     = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen]         = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);

  const resourcesRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close open dropdowns on any click outside their own container. A fixed
  // full-viewport backdrop doesn't work here: the header's backdrop-blur-*
  // (backdrop-filter) makes it the containing block for fixed-position
  // descendants in Chromium, so a "fixed inset-0" child only covers the
  // header's own bounding box, not the page below it.
  useEffect(() => {
    if (!showResourcesMenu && !showLanguagePopup) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (showResourcesMenu && resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) {
        setShowResourcesMenu(false);
      }
      if (showLanguagePopup && languageRef.current && !languageRef.current.contains(e.target as Node)) {
        setShowLanguagePopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showResourcesMenu, showLanguagePopup]);

  // Close dropdown on route change
  useEffect(() => {
    setShowLanguagePopup(false);
    setShowResourcesMenu(false);
    setMobileMenuOpen(false);
    setMobileResourcesOpen(false);
  }, [location.pathname]);

  const currentLang = supportedLanguages.find(
    (l) => l.language_code === currentLanguage,
  ) ?? supportedLanguages[0];

  const handleLanguageSelect = (code: string) => {
    changeLanguage(code);
    const path = location.pathname.replace(/^\/(nl|de|fr|es|it|pt|hi)(?=\/|$)/, '') || '/';
    const section: PublicSeoSection | null = path === '/' ? 'home' : path === '/about' ? 'about' : path === '/faq' ? 'faq' : null;
    if (section) navigate(localizedPublicPath(code, section), { replace: true });
    setShowLanguagePopup(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg shadow-blue-900/6"
            : "bg-white/80 backdrop-blur-sm"
        }`}
      >
        {/* Gradient bottom border */}
        <div
          className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent"
          aria-hidden="true"
        />

        <div className="max-w-7xl mx-auto h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8">

          {/* ── Logo ─────────────────────────────────────────── */}
          <Link to={localizedPublicPath(currentLanguage, 'home')} className="flex items-center gap-2.5 group flex-shrink-0">
            <img
              src={logo}
              alt="DysTest"
              className="h-9 w-auto transition-all duration-300 group-hover:opacity-90 group-hover:scale-[1.03]"
            />
          </Link>

          {/* ── Centre nav (desktop) ──────────────────────────── */}
          <nav
            className="hidden md:flex items-center gap-1"
            aria-label="Main navigation"
          >
            {NAV_LINKS.map(({ label, to }) => {
              const active = location.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`relative px-4 py-2 rounded-xl text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                    active
                      ? "text-blue-700"
                      : "text-gray-600 hover:text-blue-700"
                  }`}
                >
                  {active && (
                    <span
                      className="absolute inset-0 rounded-xl bg-blue-50 border border-blue-100"
                      style={{ zIndex: -1 }}
                    />
                  )}
                  {label}
                </Link>
              );
            })}

            {/* ── Resources dropdown ─────────────────────────── */}
            <div className="relative" ref={resourcesRef}>
              <button
                onClick={() => setShowResourcesMenu((p) => !p)}
                aria-expanded={showResourcesMenu}
                aria-haspopup="menu"
                className={`relative flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                  resourcesActive || showResourcesMenu
                    ? "text-blue-700"
                    : "text-gray-600 hover:text-blue-700"
                }`}
              >
                {(resourcesActive || showResourcesMenu) && (
                  <span
                    className="absolute inset-0 rounded-xl bg-blue-50 border border-blue-100"
                    style={{ zIndex: -1 }}
                  />
                )}
                {t('nav_resources')}
                <svg
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${showResourcesMenu ? "rotate-180" : ""}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showResourcesMenu && (
                  <div
                    className="absolute left-0 top-[calc(100%+10px)] w-64 bg-white rounded-2xl overflow-hidden z-[9999]"
                    style={{ boxShadow: "0 8px 40px -4px rgba(0,0,0,0.18), 0 2px 12px -2px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)" }}
                    role="menu"
                    aria-label="Resources"
                  >
                    <div className="py-2">
                      {RESOURCES_LINKS.map(({ label, to, icon: Icon, isActive }) => {
                        return (
                          <Link
                            key={to}
                            to={to}
                            role="menuitem"
                            onClick={() => setShowResourcesMenu(false)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 focus:outline-none group ${
                              isActive
                                ? "bg-blue-50 border-l-[3px] border-blue-500"
                                : "border-l-[3px] border-transparent hover:bg-gray-50/80"
                            }`}
                          >
                            <Icon
                              className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? "text-blue-600" : "text-gray-400 group-hover:text-blue-500"}`}
                              strokeWidth={2}
                              aria-hidden="true"
                            />
                            <span className={`flex-1 text-sm font-semibold ${isActive ? "text-blue-700" : "text-gray-800 group-hover:text-gray-900"}`}>
                              {label}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>

            {/* ── Dyslexie font (external partner link) ─────────── */}
            <span className="mx-2 h-6 w-px bg-gray-200" aria-hidden="true" />
            <a
              href="https://dyslexiefont.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Dyslexie font (opens in a new tab)"
              title="dyslexiefont.com"
              className="group flex items-center gap-1 px-2 py-1.5 rounded-xl transition-colors duration-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <img src="/logos/dyslexiefont.png" alt="dyslexiefont.com" width={73} height={28} className="h-7 w-auto" />
              <ArrowUpRight className="w-3.5 h-3.5 text-gray-400 transition-colors group-hover:text-blue-600" strokeWidth={2.5} aria-hidden="true" />
            </a>
          </nav>

          {/* ── Right cluster ─────────────────────────────────── */}
          <div className="flex items-center gap-3">

            {/* ── Language selector ──────────────────────────── */}
            {supportedLanguages.length > 0 && (
              <div className="relative" ref={languageRef}>
                <button
                  onClick={() => setShowLanguagePopup((p) => !p)}
                  aria-expanded={showLanguagePopup}
                  aria-haspopup="listbox"
                  aria-label="Change assessment language"
                  title="Assessment language"
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                    showLanguagePopup
                      ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200/60"
                      : "bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50/50 shadow-sm"
                  }`}
                >
                  {currentLang?.flag_image_url && (
                    <img
                      src={currentLang.flag_image_url}
                      alt=""
                      width={20}
                      height={14}
                      className="w-5 h-3.5 rounded-sm object-cover flex-shrink-0"
                    />
                  )}
                  <span className="hidden sm:block max-w-[90px] truncate">
                    {nativeLanguageName(currentLanguage, currentLanguageName)}
                  </span>
                  <span className="sm:hidden text-xs font-mono">
                    {currentLanguage.toUpperCase()}
                  </span>
                  {/* Chevron */}
                  <svg
                    className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${showLanguagePopup ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Language dropdown */}
                {showLanguagePopup && (
                      <div
                        className="absolute right-0 top-[calc(100%+10px)] w-72 bg-white rounded-2xl overflow-hidden z-[9999]"
                        style={{ boxShadow: "0 8px 40px -4px rgba(0,0,0,0.18), 0 2px 12px -2px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)" }}
                        role="listbox"
                        aria-label="Select assessment language"
                      >
                        {/* Dropdown gradient header */}
                        <div className="relative px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 overflow-hidden">
                          <div
                            className="absolute inset-0 opacity-[0.08]"
                            style={{
                              backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                              backgroundSize: "20px 20px",
                            }}
                            aria-hidden="true"
                          />
                          <p className="relative text-white font-bold text-sm">
                            {t('nav_langMenuTitle')}
                          </p>
                          <p className="relative text-white/70 text-xs mt-0.5">
                            {t('nav_langMenuHint')}
                          </p>
                        </div>

                        {/* Language list */}
                        <div className="py-2 max-h-64 overflow-y-auto">
                          {supportedLanguages.map((lang) => {
                            const isActive = lang.language_code === currentLanguage;
                            return (
                              <button
                                key={lang.id}
                                role="option"
                                aria-selected={isActive}
                                onClick={() => handleLanguageSelect(lang.language_code)}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 focus:outline-none group ${
                                  isActive
                                    ? "bg-blue-50 border-l-[3px] border-blue-500"
                                    : "border-l-[3px] border-transparent hover:bg-gray-50/80"
                                }`}
                              >
                                {lang.flag_image_url && (
                                  <img
                                    src={lang.flag_image_url}
                                    alt=""
                                    className="w-7 h-5 rounded object-cover shadow-sm flex-shrink-0"
                                  />
                                )}
                                <span
                                  className={`flex-1 text-sm font-semibold ${
                                    isActive ? "text-blue-700" : "text-gray-800 group-hover:text-gray-900"
                                  }`}
                                >
                                  {nativeLanguageName(lang.language_code, lang.language_name)}
                                </span>
                                <span
                                  className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-md ${
                                    isActive
                                      ? "bg-blue-100 text-blue-600"
                                      : "bg-gray-100 text-gray-400"
                                  }`}
                                >
                                  {lang.language_code.toUpperCase()}
                                </span>
                                {isActive && (
                                  <svg
                                    className="w-4 h-4 text-blue-500 flex-shrink-0"
                                    fill="currentColor" viewBox="0 0 20 20"
                                    aria-hidden="true"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                  )}
              </div>
            )}

            {/* ── Primary action: start the test (desktop) ──────── */}
            <button
              type="button"
              onClick={startScreening}
              className="group hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold shadow-md shadow-blue-200/60 transition-all duration-200 hover:shadow-lg hover:shadow-blue-300/60 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
            >
              {t('nav_startAssessment')}
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={2.5} aria-hidden="true" />
            </button>

            {/* ── Mobile menu button ─────────────────────────── */}
            <button
              className="md:hidden flex flex-col gap-1.5 p-2 rounded-xl hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
              onClick={() => setMobileMenuOpen((p) => !p)}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle mobile menu"
            >
              <span
                className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-200 ${mobileMenuOpen ? "translate-y-2 rotate-45" : ""}`}
              />
              <span
                className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-200 ${mobileMenuOpen ? "opacity-0" : ""}`}
              />
              <span
                className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-200 ${mobileMenuOpen ? "-translate-y-2 -rotate-45" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* ── Mobile nav panel ─────────────────────────────────── */}
        {mobileMenuOpen && (
            <div
              className="md:hidden border-t border-gray-100 bg-white/95 backdrop-blur-md"
            >
              <nav className="px-4 py-3 flex flex-col gap-1" aria-label="Mobile navigation">
                <button
                  type="button"
                  onClick={startScreening}
                  className="mb-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold shadow-md shadow-blue-200/60 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  {t('nav_startAssessment')}
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
                </button>
                {NAV_LINKS.map(({ label, to }) => {
                  const active = location.pathname.startsWith(to);
                  return (
                    <Link
                      key={to}
                      to={to}
                      className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                        active
                          ? "bg-blue-50 text-blue-700 border border-blue-100"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {label}
                    </Link>
                  );
                })}

                {/* Resources accordion */}
                <button
                  onClick={() => setMobileResourcesOpen((p) => !p)}
                  aria-expanded={mobileResourcesOpen}
                  className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    resourcesActive
                      ? "bg-blue-50 text-blue-700 border border-blue-100"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t('nav_resources')}
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${mobileResourcesOpen ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileResourcesOpen && (
                    <div
                      className="flex flex-col gap-1 pl-4 overflow-hidden"
                    >
                      {RESOURCES_LINKS.map(({ label, to, icon: Icon, isActive }) => (
                        <Link
                          key={to}
                          to={to}
                          className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                            isActive
                              ? "bg-blue-50 text-blue-700 border border-blue-100"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                          {label}
                        </Link>
                      ))}
                    </div>
                  )}

                {/* Dyslexie font (external partner link) */}
                <div className="my-1 h-px bg-gray-100" aria-hidden="true" />
                <a
                  href="https://dyslexiefont.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Dyslexie font (opens in a new tab)"
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl hover:bg-gray-50"
                >
                  <img src="/logos/dyslexiefont.png" alt="dyslexiefont.com" width={73} height={28} className="h-7 w-auto" />
                  <ArrowUpRight className="w-3.5 h-3.5 text-gray-400" strokeWidth={2.5} aria-hidden="true" />
                </a>
              </nav>
            </div>
          )}
      </header>
    </>
  );
};

export default Header;
